import { useTranslation } from "react-i18next";
import { FileUp } from "lucide-react";
import type { OrganizationMemberRoleName } from "@multi-chat/shared";
import { isApiError } from "../../api/client.js";
import { ErrorState } from "../../components/common/ErrorState/index.js";
import { ContactImportReportTable } from "./ContactImportReportTable.js";
import { ContactImportReportToolbar } from "./ContactImportReportToolbar.js";
import { ReportColumnsPopover } from "./ReportColumnsPopover.js";
import { ReportPageChrome } from "./ReportPageChrome.js";
import { ReportPageHeader } from "./ReportPageHeader.js";
import { ReportRefreshButton } from "./ReportRefreshButton.js";
import { contactImportReportEmptyKind } from "./report-empty-kind.js";
import {
  CONTACT_IMPORT_REPORT_COLUMNS,
  type ContactImportReportColumnId,
} from "./report-column-defs.js";
import { useContactImportReport } from "./use-contact-import-report.js";
import { useReportTableLayout } from "./use-report-table-layout.js";

function columnLabel(t: (key: string) => string, id: ContactImportReportColumnId): string {
  switch (id) {
    case "file":
    case "date":
    case "rows":
    case "created":
    case "restored":
    case "skipped":
    case "failed":
    case "warnings":
    case "report":
      return t(`reports.contactImports.columns.${id}`);
    default: {
      const _exhaustive: never = id;
      return _exhaustive;
    }
  }
}

export function ContactImportReportsPage({
  organizationId,
  role,
}: {
  organizationId: number;
  role: OrganizationMemberRoleName;
}): React.JSX.Element {
  const { t } = useTranslation();
  const report = useContactImportReport({ organizationId, role });
  const layout = useReportTableLayout(CONTACT_IMPORT_REPORT_COLUMNS);

  const errorMessage =
    report.error == null
      ? null
      : isApiError(report.error) && report.error.status === 0
        ? t("reports.contactImports.backendUnavailable")
        : isApiError(report.error) && report.error.status === 403
          ? t("reports.contactImports.forbidden")
          : t("reports.contactImports.loadFailed");

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden" data-testid="reports-contact-imports-page">
      <ReportPageChrome>
        <ReportPageHeader
          icon={FileUp}
          title={t("reports.contactImports.title")}
          description={t("reports.contactImports.helper")}
          actions={
            report.canAccess ? (
              <ReportRefreshButton
                refreshing={Boolean(report.loading && report.list)}
                disabled={report.loading}
                onRefresh={report.refresh}
                testId="reports-contact-imports-refresh"
              />
            ) : null
          }
        />
        <ContactImportReportToolbar
          query={report.query}
          dateFrom={report.dateFrom}
          dateTo={report.dateTo}
          filteredTotal={report.list?.totalCount ?? null}
          filtersActive={report.filtersActive}
          onSearchChange={report.setSearch}
          onDateRangeChange={report.setDateRange}
          onResetFilters={report.resetFilters}
          columnsSlot={
            <ReportColumnsPopover
              columnOrder={layout.columnOrder}
              hiddenColumns={layout.hiddenColumns}
              requiredColumns={CONTACT_IMPORT_REPORT_COLUMNS.requiredColumns}
              labelFor={(id) => columnLabel(t, id)}
              onToggle={layout.toggleColumn}
              onMove={layout.moveColumn}
              onReorder={layout.reorderColumn}
              onReset={layout.resetColumns}
              testIdPrefix="reports-contact-imports"
            />
          }
        />
      </ReportPageChrome>

      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-4 py-3 md:overflow-hidden md:px-5">
        {errorMessage ? (
          <ErrorState
            layout="panel"
            message={errorMessage}
            onRetry={report.refresh}
            retryLabel={t("auth.retry")}
          />
        ) : null}

        <div className="flex min-h-[20rem] min-h-0 flex-1 flex-col md:min-h-0">
          <ContactImportReportTable
            organizationId={organizationId}
            list={report.list}
            loading={report.loading}
            page={report.query.page}
            pageSize={report.query.pageSize}
            emptyKind={contactImportReportEmptyKind(report.query)}
            visibleColumns={layout.visibleColumns}
            widths={layout.widths}
            onResizeColumn={layout.resizeColumn}
            onReorderColumn={layout.reorderColumn}
            onPageChange={report.setPage}
            onPageSizeChange={report.setPageSize}
            onResetFilters={report.resetFilters}
          />
        </div>
      </div>
    </div>
  );
}
